import { usePawnMode } from "../../Context/PawnModeContext";
import { useAccountsNav } from "../../utils/moduleNav";

/**
 * Which pawn BOOK the shared Accounts reports must read.
 *
 * These screens came over from the Travel ERP, where a transaction posts to the
 * ledger exactly once. Pawn is different: `ledger.status` records the book a row
 * belongs to —
 *
 *   '1'  the official book of an ALTERED entry (interest capped to the store's
 *        master rate, the excess redirected to cash)
 *   '2'  the unofficial book — the true amounts as they were entered
 *   '3'  a clean entry, within the 1.16% cap and with no one-month deduction,
 *        that both books agree on, so it is posted once and SHARED
 *
 * An official payment charged above the cap therefore writes BOTH a '1' and a
 * '2' row for the same voucher at different amounts. Reading every row — which
 * is what these reports did — lists that voucher twice and inflates every total.
 *
 *   official mode   → the '1' and '3' rows
 *   unofficial mode → the '2' and '3' rows
 *
 * The mode travels to the API as `pawn_mode`, and only from inside the pawn
 * module: Daily Tick and the Travel ERP send nothing and are read unfiltered,
 * exactly as before (their rows use a different status vocabulary entirely).
 *
 * Usage:
 *   const { pawnMode, scope } = usePawnLedgerScope();
 *   axios.post(url, { ...payload, ...scope });
 *   useEffect(() => { buildReport(); }, [pawnMode]);
 *
 * Consumers: the three reports under ./pawnLedger and AccountEntry's voucher
 * drill-down. The Travel ERP copies of these reports never see a pawn store, so
 * they send nothing and are read unfiltered.
 *
 * See project-pawn-mode-isolation for the status model, and pawnBookWhere() in
 * the backend's ledgerController for the query side of the same rule.
 */
export const usePawnLedgerScope = () => {
  const { inPawn } = useAccountsNav();
  const { mode } = usePawnMode() || {};

  // Fail safe: anything that isn't an explicit unofficial reads as official, so
  // a missing/malformed mode can never leak the unofficial books.
  const pawnMode = inPawn ? (parseInt(mode, 10) === 2 ? 2 : 1) : null;

  return { inPawn, pawnMode, scope: pawnMode ? { pawn_mode: pawnMode } : {} };
};

export default usePawnLedgerScope;
